/**
 * Getting `?credit_token=` out of the URL before anything can report the URL.
 *
 * The token authorises spending money for two hours. `credit-token.ts` already
 * strips it — but from a React mount effect, and the Meta Pixel's inline
 * `PageView` in `app/layout.tsx` fires before that, sending the document
 * location to Facebook with a live credential in it. The same leak was closed
 * for Sentry (`redact-url.ts`) and for the shop URL after the strip; this path
 * was missed because it is a matter of ORDER, and order is not something a
 * mount effect can win against a script tag.
 *
 * So the fix does not try to win the race — it removes it. This source is
 * inlined TWICE:
 *
 *   1. as a plain `<script>` in `<head>`, which the parser runs before any
 *      body content, before hydration and before `next/script` has scheduled
 *      anything; and
 *   2. at the top of the Meta Pixel's own inline script, immediately before
 *      `fbq('init')` / `fbq('track', 'PageView')`.
 *
 * (2) is what makes this robust rather than merely early: whatever else runs
 * first, the parameter is gone from `window.location` in the same synchronous
 * block that fbq then reads it in. It is written to be idempotent so that
 * running twice is indistinguishable from running once.
 *
 * Because the strip now happens before React exists, the token would be gone by
 * the time `CreditClaimBanner` looks for it — so the guard HANDS IT OFF rather
 * than destroying it, and `captureCreditFromUrl` consumes the hand-off. That is
 * the only reason this file knows anything about credit at all.
 *
 * `credit_error` is deliberately left alone. It is a reason code, not a
 * credential, `captureCreditFromUrl` still needs it to say what went wrong, and
 * removing it here would cost the customer the explanation.
 *
 * The script is a STRING, not a function passed through `.toString()`: what
 * ships is then exactly what is tested, with no transpiler or minifier between
 * the two, and no way for it to capture a module-scope identifier that will not
 * exist in the document. `credit-url-guard.test.ts` evaluates this very string
 * through `new Function`, which gives it nothing but the globals.
 */

/** Where the guard leaves the token for `captureCreditFromUrl` to collect. */
export const CREDIT_TOKEN_HANDOFF_KEY = "credit_claim_handoff_v1";

/** The same value, as a property on `window`. See the script's comment. */
export const CREDIT_TOKEN_HANDOFF_GLOBAL = "__fdCreditClaimToken";

/**
 * Self-contained, dependency-free, and safe to inline into a `<script>` tag.
 *
 * Both hand-off slots are written, every time. sessionStorage survives a reload
 * that happens before hydration; the window property survives a browser that
 * refuses storage altogether. The URL is the only copy of the token at this
 * point, so "stash it, then strip it" must not have a path where the stash
 * silently fails and the strip goes ahead anyway.
 */
export const CREDIT_URL_GUARD_SCRIPT = `(function(){try{
var w=window;var s=w.location.search;
if(!s||s.indexOf('credit_token=')===-1)return;
var p=new URLSearchParams(s);var t=p.get('credit_token');
if(!t)return;
w['${CREDIT_TOKEN_HANDOFF_GLOBAL}']=t;
try{w.sessionStorage.setItem('${CREDIT_TOKEN_HANDOFF_KEY}',t);}catch(e){}
p.delete('credit_token');var q=p.toString();
w.history.replaceState(w.history.state,'',w.location.pathname+(q?'?'+q:'')+w.location.hash);
}catch(e){}})();`;

/** The token the guard took out of the URL, or null. */
export function readCreditTokenHandoff(): string | null {
  if (typeof window === "undefined") return null;
  const onWindow = (window as unknown as Record<string, unknown>)[
    CREDIT_TOKEN_HANDOFF_GLOBAL
  ];
  if (typeof onWindow === "string" && onWindow) return onWindow;
  try {
    return window.sessionStorage.getItem(CREDIT_TOKEN_HANDOFF_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Empty both slots.
 *
 * Called once the token has been moved into its real two-hour home, so that a
 * later load of the same tab cannot find a stale one and offer credit that the
 * server will refuse.
 */
export function clearCreditTokenHandoff(): void {
  if (typeof window === "undefined") return;
  delete (window as unknown as Record<string, unknown>)[
    CREDIT_TOKEN_HANDOFF_GLOBAL
  ];
  try {
    window.sessionStorage.removeItem(CREDIT_TOKEN_HANDOFF_KEY);
  } catch {
    // Private mode, quota, disabled storage — nothing to clear then either.
  }
}
